import { Box, Card, IconButton, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Icon } from '@verantos-dev/shared-components';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { useStudyPeriodSection } from './useStudyPeriodSection';
import { StudyPeriodInlineEditForm } from '../../../containers/v0/cohort-builder-sections/StudyPeriodSectionV2/components/StudyPeriodInlineEditForm';
import { useStudyPeriodInlineEdit } from '../../../containers/v0/cohort-builder-sections/StudyPeriodSectionV2/hooks/useStudyPeriodInlineEdit';

dayjs.extend(utc);

// Store may hold either a date-only `YYYY-MM-DD` (hydrated from the API) or a
// full ISO datetime (after inline-edit calls `Dayjs.toISOString()`). Parse via
// dayjs.utc so both shapes render the same MM/DD/YYYY — matches the legacy v0
// study period view (useStudyPeriodViewCard).
const formatDate = (value: string | undefined): string => {
  if (!value) return '—';
  const parsed = dayjs.utc(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : value;
};

interface StudyPeriodSectionProps {
  isLoading?: boolean;
}

/**
 * Study period card for the gen-3 cohort detail Definition tab.
 *
 * Mostly read-only display, but reuses the legacy v0 inline-edit form
 * + hook (`useStudyPeriodInlineEdit`, `StudyPeriodInlineEditForm`) so
 * users can change the date range without leaving the page. Edit
 * affordance is gated by `viewMode` from the hook (processing / final /
 * non-owner cohorts hide the pencil).
 *
 * Layout mirrors the Study Period Configuration Card Figma spec (M4
 * visual audit): 24px card padding, 24px title→content gap, and the
 * edit pencil (shared `Icon icon="edit"`) lives INSIDE the gray
 * read-only box, right-aligned — not up in the card header.
 *
 * Width-fluid: card sits at 100% of the parent column and the inner
 * date pill word-wraps cleanly at narrow widths.
 */
export const StudyPeriodSection = ({ isLoading = false }: StudyPeriodSectionProps) => {
  const theme = useTheme();
  const { startDate, endDate, isDefined } = useStudyPeriodSection();
  const inlineEdit = useStudyPeriodInlineEdit();

  const canEdit = !inlineEdit.viewMode && !!inlineEdit.isEditable;

  return (
    <Card
      data-testid="cohort-detail-study-period"
      variant="outlined"
      sx={{
        padding: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        minWidth: 0,
      }}
    >
      <Typography variant="h6" component="h3">
        Study period
      </Typography>
      {inlineEdit.isEditing && canEdit ? (
        <StudyPeriodInlineEditForm
          viewMode={inlineEdit.viewMode}
          isValidForSubmit={
            !inlineEdit.hasError && !!inlineEdit.isEditable && inlineEdit.hasChanges
          }
          studyPeriodStartDate={inlineEdit.studyPeriodStartDate}
          setStudyPeriodStartDate={inlineEdit.setStudyPeriodStartDate}
          studyPeriodEndDate={inlineEdit.studyPeriodEndDate}
          setStudyPeriodEndDate={inlineEdit.setStudyPeriodEndDate}
          setHasDateBoundsError={inlineEdit.setHasDateBoundsError}
          minDateBound={inlineEdit.datasetMinDate}
          maxDateBound={inlineEdit.datasetMaxDate}
          onSave={inlineEdit.saveEdit}
          onCancel={inlineEdit.cancelEdit}
        />
      ) : (
        <Box
          data-testid="cohort-detail-study-period-range"
          sx={{
            paddingX: 6,
            paddingY: 4,
            // Figma READ ONLY VIEW: 16px vertical padding around a 48px
            // content row → 80px box.
            minHeight: 80,
            backgroundColor: theme.palette.grey[100],
            borderRadius: `${borderRadius.md}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary">
              Date range
            </Typography>
            {isLoading ? (
              <Skeleton variant="text" width={200} height={28} />
            ) : (
              <Typography variant="body1" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                {isDefined ? `${formatDate(startDate)} – ${formatDate(endDate)}` : '—'}
              </Typography>
            )}
          </Box>
          {canEdit && !isLoading && (
            <Tooltip title="Edit study period" arrow>
              <IconButton
                size="small"
                onClick={inlineEdit.startEdit}
                data-testid="cohort-detail-study-period-edit"
                aria-label="Edit study period"
              >
                <Icon
                  id="cohort-detail-study-period-edit-icon"
                  icon="edit"
                  width={20}
                  height={20}
                />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Card>
  );
};
